(() => {
  const parseJson = (node, fallback) => {
    try { return JSON.parse(node?.textContent || ''); } catch (_) { return fallback; }
  };
  const checkedValue = (root, selector) => root.querySelector(`${selector}:checked`)?.value || '';
  const setHidden = (node, hidden) => {
    if (!node) return;
    node.hidden = hidden;
    if ('disabled' in node) node.disabled = hidden;
    node.querySelectorAll?.('input, select, textarea, button').forEach((field) => { field.disabled = hidden; });
  };
  const money = (cents, currency = 'AUD') => {
    try { return new Intl.NumberFormat('en-AU', { style: 'currency', currency }).format((Number(cents) || 0) / 100); }
    catch (_) { return `$${((Number(cents) || 0) / 100).toFixed(2)}`; }
  };
  const moneyWithCurrency = (cents, currency = 'AUD') => `${money(cents, currency)} ${currency}`;
  const compactMoney = (cents, currency = 'AUD') => {
    const amount = (Number(cents) || 0) / 100;
    try {
      return new Intl.NumberFormat('en-AU', {
        style: 'currency', currency, currencyDisplay: 'narrowSymbol',
        minimumFractionDigits: Number(cents) % 100 === 0 ? 0 : 2, maximumFractionDigits: 2
      }).format(amount);
    } catch (_) { return `$${amount.toFixed(Number(cents) % 100 === 0 ? 0 : 2)}`; }
  };
  const escapeHtml = (value) => String(value ?? '').replace(/[&<>'"]/g, (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' }[character]));
  const row = (label, value) => `<div><dt>${escapeHtml(label)}</dt><dd>${escapeHtml(value)}</dd></div>`;

  class OpticalPrescriptionBuilder extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('[data-optical-rx-dialog]');
      this.body = this.querySelector('[data-optical-rx-body]');
      this.nextButton = this.querySelector('[data-optical-rx-next]');
      this.backButton = this.querySelector('[data-optical-rx-back]');
      this.errorNode = this.querySelector('[data-optical-rx-error]');
      this.services = parseJson(this.querySelector('[data-optical-rx-services]'), {});
      this.variants = parseJson(this.querySelector('[data-optical-rx-variants]'), []);
      this.currency = this.dataset.currency || 'AUD';
      this.comboDiscount = Number(this.dataset.comboDiscountCents || 0);
      this.prismUpgrade = {
        id: Number(this.dataset.prismVariantId || 0),
        price: Number(this.dataset.prismPrice || 5000),
        available: this.dataset.prismAvailable === 'true'
      };
      this.minimumIndex = this.dataset.minimumIndex || 't150';
      this.step = 1;
      this.adding = false;
      this.state = {
        vision: '', use: '', design: '', category: '', thickness: '', coating: '', light: '', sun: '', tint: '',
        prescriptionMethod: '', prism: 'no', pdMethod: '', acknowledgement: false,
        uploads: { prescription: '' }, clinical: {}, pdSource: '', pdProvenance: null
      };
      this.currentVariantId = Number(this.dataset.frameVariantId || 0);
      this.populatePrismSelects();
      this.bind();
      this.subscribeToVariantChanges();
      this.adoptCloudliftUploads();
      this.uploadProviderSignature = this.getUploadProviderSignature();
      this.uploadObserver = new MutationObserver(() => {
        const signature = this.getUploadProviderSignature();
        if (signature === this.uploadProviderSignature) return;
        this.uploadProviderSignature = signature;
        this.adoptCloudliftUploads();
      });
      this.querySelectorAll('[data-optical-rx-upload-slot]').forEach((slot) => {
        this.uploadObserver.observe(slot, { childList: true, subtree: true });
      });
      this.uploadAvailabilityTimer = window.setTimeout(() => { this.updateUploadAvailability(true); this.render(); }, 3500);
      this.updateDerivedPrices();
      this.render();
    }

    disconnectedCallback() {
      this.uploadObserver?.disconnect();
      window.clearTimeout(this.uploadAvailabilityTimer);
      if (typeof this.unsubscribe === 'function') this.unsubscribe();
      this.pdController?.destroy?.();
      if (this.documentClickHandler) document.removeEventListener('click', this.documentClickHandler, true);
      if (this.documentKeydownHandler) document.removeEventListener('keydown', this.documentKeydownHandler);
    }

    bind() {
      this.documentClickHandler = (event) => {
        const opener = event.target.closest('[data-optical-prescription-open], [data-optical-prescription-launch]');
        if (!opener) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.open(opener);
      };
      document.addEventListener('click', this.documentClickHandler, true);
      this.querySelector('[data-optical-rx-close]')?.addEventListener('click', () => this.close());
      this.dialog?.addEventListener('cancel', (event) => { event.preventDefault(); this.close(); });
      this.dialog?.addEventListener('click', (event) => { if (event.target === this.dialog) this.close(); });
      this.nextButton?.addEventListener('click', () => this.forward());
      this.backButton?.addEventListener('click', () => this.backward());
      this.addEventListener('change', (event) => this.onChange(event));
      this.addEventListener('input', (event) => this.onInput(event));
      this.querySelectorAll('[data-optical-rx-edit]').forEach((button) => button.addEventListener('click', () => this.goTo(Number(button.dataset.opticalRxEdit))));
      this.querySelector('[data-optical-rx-pd-open]')?.addEventListener('click', (event) => this.openPdCamera(event.currentTarget));
      this.sunComparison = this.querySelector('[data-optical-rx-sun-comparison]');
      this.sunDetailsButton = this.querySelector('[data-optical-rx-sun-details]');
      this.sunDetailsButton?.addEventListener('click', () => this.openSunComparison());
      this.querySelector('[data-optical-rx-sun-comparison-close]')?.addEventListener('click', () => this.sunComparison?.close());
      this.sunComparison?.addEventListener('cancel', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.sunComparison.close();
      });
      this.sunComparison?.addEventListener('click', (event) => {
        if (event.target === this.sunComparison) this.sunComparison.close();
      });
      this.sunComparison?.addEventListener('close', () => this.sunDetailsButton?.focus());
      this.addEventListener('rx-pd-measurement-used', (event) => this.applyPdMeasurement(event.detail));
      this.addEventListener('rx-pd-manual-requested', () => {
        this.state.pdMethod = this.state.vision === 'mf' ? 'dual' : 'single';
        this.syncRadio('optical-rx-pd-method-', this.state.pdMethod);
        this.renderMeasurements();
      });
      this.documentKeydownHandler = (event) => this.trapFocus(event);
      document.addEventListener('keydown', this.documentKeydownHandler);
    }

    subscribeToVariantChanges() {
      const picker = document.querySelector(`variant-selects[data-section="${CSS.escape(this.dataset.sectionId)}"][data-city-optical-size-index]`);
      if (!picker || typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
      this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        if (!event.data || String(event.data.sectionId) !== String(this.dataset.sectionId)) return;
        const variant = event.data.variant;
        if (!variant) return;
        this.currentVariantId = Number(variant.id);
        const record = this.variants.find((item) => Number(item.id) === this.currentVariantId);
        if (record) {
          record.price = Number(variant.price || record.price);
          record.available = Boolean(variant.available);
        }
        this.render();
      });
    }

    currentFrame() {
      return this.variants.find((variant) => Number(variant.id) === Number(this.currentVariantId)) || this.variants[0] || null;
    }

    framePreview(frame = this.currentFrame()) {
      if (!frame) return this.dataset.frameImage || '';
      if (this.state.category === 'sun') {
        const tintKey = String(this.state.tint || '').trim().toLowerCase();
        if (tintKey === 'grey' || tintKey === 'brown') {
          return frame[tintKey] || frame.image || this.dataset.frameImage || '';
        }
      }
      return frame.image || this.dataset.frameImage || '';
    }

    open(opener) {
      if (this.dialog?.open || this.dataset.opticalRxOpening === 'true') return;
      const purchaseShell = document.querySelector('optical-lens-shell');
      const selectedPurpose = purchaseShell?.querySelector('[data-optical-purpose]:checked');
      if (selectedPurpose && selectedPurpose.dataset.opticalPurpose !== 'standard') {
        const standardPurpose = purchaseShell.querySelector('[data-optical-purpose="standard"]');
        if (standardPurpose && !standardPurpose.disabled) {
          standardPurpose.click();
          let notice = document.querySelector('[data-city-rx-reset-notice]');
          if (!notice) {
            notice = document.createElement('div');
            notice.dataset.cityRxResetNotice = 'true';
            notice.className = 'city-rx-reset-notice';
            notice.setAttribute('role', 'status');
            notice.setAttribute('aria-live', 'polite');
            document.body.appendChild(notice);
          }
          notice.textContent = 'Non-prescription lens upgrade removed. Choose your prescription lens options in the next step.';
          notice.classList.add('is-visible');
          window.clearTimeout(this.rxResetNoticeTimer);
          this.rxResetNoticeTimer = window.setTimeout(() => notice.classList.remove('is-visible'), 4200);
        }
      }
      this.syncCurrentFrameFromProductForm();
      const frame = this.currentFrame();
      if (!frame?.available) { this.setExternalStatus('This frame colour is currently unavailable.'); return; }
      this.dataset.opticalRxOpening = 'true';
      this.opener = opener;
      this.goTo(1, false);
      if (!this.dialog.open) this.dialog.showModal();
      document.documentElement.classList.add('optical-rx-open');
      this.trackEntry('optical_prescription_builder_opened', opener);
      delete this.dataset.opticalRxOpening;
      requestAnimationFrame(() => this.querySelector('[data-optical-rx-step="1"] h3')?.focus());
    }

    close({ restoreFocus = true } = {}) {
      this.pdController?.close?.(false, 'builder_closed');
      if (this.sunComparison?.open) this.sunComparison.close();
      if (this.dialog?.open) this.dialog.close();
      document.documentElement.classList.remove('optical-rx-open');
      if (restoreFocus) this.opener?.focus?.();
    }

    syncCurrentFrameFromProductForm() {
      const sectionId = String(this.dataset.sectionId || '');
      const sectionForm = sectionId
        ? document.querySelector(`product-form[data-section-id="${CSS.escape(sectionId)}"] input.product-variant-id`)
        : null;
      const variantInput = sectionForm || document.querySelector('product-form input.product-variant-id');
      const variantId = Number(variantInput?.value || 0);
      if (variantId && this.variants.some((variant) => Number(variant.id) === variantId)) this.currentVariantId = variantId;
    }

    trackEntry(eventName, opener) {
      const detail = {
        source: opener?.dataset.opticalPrescriptionSource || 'unknown',
        product_id: Number(this.dataset.productId || 0),
        variant_id: Number(this.currentVariantId || 0)
      };
      document.dispatchEvent(new CustomEvent('city:optical-purchase-action', { detail: { action: eventName, ...detail } }));
      const privacy = window.Shopify?.customerPrivacy;
      let analyticsAllowed = false;
      try {
        analyticsAllowed = typeof privacy?.analyticsProcessingAllowed === 'function' && privacy.analyticsProcessingAllowed() === true;
      } catch (_) {
        analyticsAllowed = false;
      }
      if (analyticsAllowed && Array.isArray(window.dataLayer)) window.dataLayer.push({ event: eventName, ...detail });
    }

    trapFocus(event) {
      if (event.key !== 'Tab' || !this.dialog?.open || !this.contains(document.activeElement)) return;
      const activePd = this.querySelector('[data-optical-rx-pd-tool]:not([hidden])');
      const scope = this.sunComparison?.open ? this.sunComparison : activePd || this.dialog;
      const nodes = [...scope.querySelectorAll('button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')].filter((node) => !node.hidden && node.offsetParent !== null);
      if (!nodes.length) return;
      const first = nodes[0]; const last = nodes[nodes.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    }

    openSunComparison() {
      if (!this.sunComparison || this.sunComparison.open || typeof this.sunComparison.showModal !== 'function') return;
      this.sunComparison.showModal();
    }

    onChange(event) {
      const input = event.target;
      if (input.matches('[name^="optical-rx-vision-"]')) {
        this.state.vision = input.value;
        if (input.value === 'sv') this.state.design = '';
        else this.state.use = '';
      } else if (input.matches('[name^="optical-rx-use-"]')) this.state.use = input.value;
      else if (input.matches('[name^="optical-rx-design-"]')) this.state.design = input.value;
      else if (input.matches('[name^="optical-rx-category-"]')) this.selectCategory(input.value);
      else if (input.matches('[name^="optical-rx-thickness-"]')) this.state.thickness = input.value;
      else if (input.matches('[name^="optical-rx-coating-"]')) {
        this.coatingChosen = true;
        this.selectCoating(input.value);
      } else if (input.matches('[name^="optical-rx-light-"]')) this.state.light = input.value;
      else if (input.matches('[name^="optical-rx-sun-"]')) this.state.sun = input.value;
      else if (input.matches('[name^="optical-rx-tint-"]')) this.state.tint = input.value;
      else if (input.matches('[name^="optical-rx-rx-method-"]')) this.state.prescriptionMethod = input.value;
      else if (input.matches('[name^="optical-rx-prism-"]')) this.state.prism = input.value;
      else if (input.matches('[name^="optical-rx-pd-method-"]')) {
        this.state.pdMethod = input.value;
        if (input.value === 'camera') this.state.pdSource = '';
      } else if (input.matches('[data-optical-rx-ack]')) {
        this.state.acknowledgement = input.checked;
        if (input.checked) this.clearFittingAcknowledgementError();
      }
      if (input.matches('[data-optical-rx-clinical]')) this.state.clinical[input.dataset.opticalRxClinical] = String(input.value || '').trim();
      if (this.coatingDefaulted && !this.coatingChosen
        && new URLSearchParams(location.search).get('purpose') === 'blue-light-rx'
        && ['clear', 'light'].includes(this.state.category)
        && ['category', 'thickness', 'light'].some((field) => input.matches(`[name^="optical-rx-${field}-"]`))) {
        this.selectCoating(this.defaultCoating());
      }
      this.clearError();
      this.render();
    }

    onInput(event) {
      const field = event.target.closest('[data-optical-rx-clinical]');
      if (field) this.state.clinical[field.dataset.opticalRxClinical] = field.value.trim();
      const uploadSlot = event.target.closest('[data-optical-rx-upload-slot]');
      if (uploadSlot) this.captureUpload(uploadSlot, uploadSlot.dataset.opticalRxUploadSlot);
      this.clearError();
      this.updateForwardState();
    }

    selectCategory(category) {
      this.state.category = category;
      if (!this.state.thickness || (this.minimumIndex === 't160' && this.state.thickness === 'T150')) this.state.thickness = this.minimumIndex === 't160' ? 'T160' : 'T150';
      if (category === 'clear') {
        if (!this.state.coating) this.selectCoating(this.defaultCoating());
        this.state.light = ''; this.state.sun = ''; this.state.tint = '';
      } else if (category === 'light') {
        if (!this.state.light) this.state.light = 'STD';
        if (!this.state.coating) this.selectCoating(this.defaultCoating());
        this.state.sun = ''; this.state.tint = '';
      } else if (category === 'sun') {
        if (!this.state.sun) this.state.sun = 'TINT';
        if (!this.state.tint) this.state.tint = 'Grey';
        this.state.coating = ''; this.state.light = '';
      }
      this.syncSelections();
    }

    defaultCoating() {
      const params = new URLSearchParams(location.search);
      if (params.get('purpose') !== 'blue-light-rx' || this.coatingChosen
        || ['rx_edit', 'cart_edit', 'cart_edit_key'].some((key) => params.has(key))
        || !['clear', 'light'].includes(this.state.category)
        || !this.state.vision || !(this.state.vision === 'sv' ? this.state.use : this.state.design)) return 'HC';
      const suffix = this.state.category === 'clear'
        ? `CLEAR-${this.state.thickness}`
        : `LIGHT-${this.state.thickness}-${this.state.light}`;
      const blue = this.service(`${this.prefix()}-${suffix}-BLUE-MTC`);
      return blue?.id && blue.available && Number.isFinite(Number(blue.price)) && Number(blue.price) > 0 ? 'BLUE-MTC' : 'HC';
    }

    selectCoating(coating) {
      this.state.coating = coating;
      this.coatingDefaulted = !this.coatingChosen;
    }

    syncSelections() {
      const values = {
        'optical-rx-vision-': this.state.vision, 'optical-rx-use-': this.state.use, 'optical-rx-design-': this.state.design,
        'optical-rx-category-': this.state.category, 'optical-rx-thickness-': this.state.thickness,
        'optical-rx-coating-': this.state.coating, 'optical-rx-light-': this.state.light, 'optical-rx-sun-': this.state.sun,
        'optical-rx-tint-': this.state.tint, 'optical-rx-rx-method-': this.state.prescriptionMethod,
        'optical-rx-prism-': this.state.prism,
        'optical-rx-pd-method-': this.state.pdMethod
      };
      Object.entries(values).forEach(([prefix, value]) => this.syncRadio(prefix, value));
      const acknowledgement = this.querySelector('[data-optical-rx-ack]');
      if (acknowledgement) acknowledgement.checked = this.state.acknowledgement;
    }

    syncRadio(prefix, value) {
      this.querySelectorAll(`input[name^="${prefix}"]`).forEach((input) => { input.checked = input.value === value; });
    }

    render() {
      this.syncSelections();
      this.renderCoatingRecommendation();
      const visionChosen = Boolean(this.state.vision);
      setHidden(this.querySelector('[data-optical-rx-sv-use]'), this.state.vision !== 'sv');
      setHidden(this.querySelector('[data-optical-rx-mf-design]'), this.state.vision !== 'mf');
      const categoryChosen = Boolean(this.state.category);
      setHidden(this.querySelector('[data-optical-rx-thickness]'), !categoryChosen);
      setHidden(this.querySelector('[data-optical-rx-coating]'), !categoryChosen || this.state.category === 'sun');
      setHidden(this.querySelector('[data-optical-rx-light]'), this.state.category !== 'light');
      setHidden(this.querySelector('[data-optical-rx-sun]'), this.state.category !== 'sun');
      setHidden(this.querySelector('[data-optical-rx-tint]'), this.state.category !== 'sun');
      this.renderMfPreview();
      this.renderLightPreview();
      this.renderSunPreview();
      const t150 = this.querySelector('input[name^="optical-rx-thickness-"][value="T150"]');
      if (t150) t150.disabled = this.minimumIndex === 't160';
      const compatibility = this.querySelector('[data-optical-rx-compatibility]');
      if (compatibility) {
        compatibility.hidden = this.minimumIndex !== 't160';
        compatibility.textContent = this.minimumIndex === 't160' ? 'This rimless frame requires Thin 1.60 or Extra Thin 1.67 lenses for suitable edge strength.' : '';
      }
      this.renderPrescription();
      this.renderMeasurements();
      this.renderFitting();
      this.updateDerivedPrices();
      this.renderReview();
      this.updateProgress();
      this.updateForwardState();
      if (!visionChosen && this.step > 1) this.goTo(1, false);
    }

    renderCoatingRecommendation() {
      const blue = new URLSearchParams(location.search).get('purpose') === 'blue-light-rx'
        || (this.coatingDefaulted && this.state.coating === 'BLUE-MTC');
      this.querySelectorAll('[data-optical-rx-coating-recommended]').forEach((badge) => {
        const recommended = badge.dataset.opticalRxCoatingRecommended === (blue ? 'BLUE-MTC' : 'MTC');
        setHidden(badge, !recommended);
        badge.closest('label').classList.toggle('optical-rx__card--badged', recommended);
      });
    }

    renderMfPreview() {
      const previewValue = this.state.design || 'STD';
      const names = { STD: 'Standard', WIDE: 'Comfort', LARGEST: 'Ultimate' };
      const alts = {
        STD: 'Standard multifocal viewing zones',
        WIDE: 'Comfort multifocal viewing zones',
        LARGEST: 'Ultimate multifocal viewing zones'
      };
      const name = this.querySelector('[data-optical-rx-mf-preview-name]');
      if (name) name.textContent = names[previewValue] || names.STD;
      this.querySelectorAll('[data-optical-rx-mf-preview-value]').forEach((image) => {
        const active = image.dataset.opticalRxMfPreviewValue === previewValue;
        image.classList.toggle('is-active', active);
        image.setAttribute('aria-hidden', String(!active));
        image.alt = active ? (alts[previewValue] || alts.STD) : '';
      });
    }

    renderLightPreview() {
      if (this.state.category !== 'light') return;
      const stage = this.querySelector('[data-optical-rx-light-preview]');
      const options = {
        STD: { name: 'Standard', src: stage?.dataset.standardSrc, alt: 'Standard light-adaptive lenses shown clear indoors and dark outdoors' },
        PREM: { name: 'Premium', src: stage?.dataset.premiumSrc, alt: 'Premium light-adaptive lenses shown clear indoors and darker outdoors' },
        XTRA: { name: 'Transitions® XTRActive®', src: stage?.dataset.xtractiveSrc, alt: 'Transitions XTRActive lenses shown slightly tinted indoors and darkest outdoors' }
      };
      this.renderCrossfadePreview(stage, this.state.light || 'STD', options, {
        image: '[data-optical-rx-light-preview-image]',
        next: '[data-optical-rx-light-preview-next]',
        name: '[data-optical-rx-light-preview-name]'
      });
    }

    renderSunPreview() {
      if (this.state.category !== 'sun') return;
      const stage = this.querySelector('[data-optical-rx-sun-preview]');
      const options = {
        TINT: { name: 'Standard tint', src: stage?.dataset.tintSrc, alt: 'Standard tint over a sunlit water scene' },
        POL: { name: 'Polarised', src: stage?.dataset.polarisedSrc, alt: 'Polarised lens view over a sunlit water scene' },
        ULTRA: { name: 'Ultra Polarised', src: stage?.dataset.ultraSrc, alt: 'Ultra Polarised lens view over a sunlit water scene' }
      };
      this.renderCrossfadePreview(stage, this.state.sun || 'TINT', options, {
        image: '[data-optical-rx-sun-preview-image]',
        next: '[data-optical-rx-sun-preview-next]',
        name: '[data-optical-rx-sun-preview-name]'
      });
    }

    renderCrossfadePreview(stage, value, options, selectors) {
      const option = options[value] || Object.values(options)[0];
      const image = stage?.querySelector(selectors.image);
      const next = stage?.querySelector(selectors.next);
      const name = stage?.querySelector(selectors.name);
      if (!stage || !option?.src || !image || !next || !name) return;
      name.textContent = option.name;
      if (image.dataset.option === value) {
        // Cancel any in-flight crossfade that would overwrite this selection.
        stage.dataset.transitionId = String((Number(stage.dataset.transitionId) || 0) + 1);
        next.onload = null;
        image.alt = option.alt;
        image.classList.remove('is-fading-out');
        next.classList.remove('is-fading-in');
        return;
      }
      const transitionId = String((Number(stage.dataset.transitionId) || 0) + 1);
      stage.dataset.transitionId = transitionId;
      const commit = () => {
        if (stage.dataset.transitionId !== transitionId) return;
        image.src = option.src;
        image.alt = option.alt;
        image.dataset.option = value;
        image.classList.remove('is-fading-out');
        next.classList.remove('is-fading-in');
      };
      if (!image.src || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        commit();
        return;
      }
      next.onload = () => {
        next.onload = null;
        if (stage.dataset.transitionId !== transitionId) return;
        window.requestAnimationFrame(() => {
          image.classList.add('is-fading-out');
          next.classList.add('is-fading-in');
          window.setTimeout(commit, 170);
        });
      };
      next.src = option.src;
      if (next.complete && next.naturalWidth) next.onload();
    }

    renderPrescription() {
      setHidden(this.querySelector('[data-optical-rx-prescription-upload]'), this.state.prescriptionMethod !== 'upload');
      setHidden(this.querySelector('[data-optical-rx-manual]'), this.state.prescriptionMethod !== 'manual');
      setHidden(this.querySelector('[data-optical-rx-prism-question]'), !this.state.prescriptionMethod);
      setHidden(this.querySelector('[data-optical-rx-prism-fields]'), this.state.prescriptionMethod !== 'manual' || this.state.prism !== 'yes');
      const multifocal = this.state.vision === 'mf';
      this.querySelectorAll('[data-optical-rx-add-field]').forEach((field) => setHidden(field, !multifocal || this.state.prescriptionMethod !== 'manual'));
      ['od', 'os'].forEach((eye) => {
        const cylinder = Number(this.state.clinical[`${eye}Cyl`] || 0);
        const axis = this.querySelector(`[data-optical-rx-clinical="${eye}Axis"]`);
        if (!axis) return;
        axis.disabled = this.state.prescriptionMethod !== 'manual' || cylinder === 0;
        if (cylinder === 0 && this.state.clinical[`${eye}Axis`]) {
          this.state.clinical[`${eye}Axis`] = '';
          axis.value = '';
        }
      });
      this.updatePrismDependencies();
      this.updateUploadAvailability(Boolean(this.uploadCheckComplete));
    }

    populatePrismSelects() {
      this.querySelectorAll('[data-rx-prism-amount]').forEach((select) => {
        if (select.options.length > 1) return;
        for (let value = 0.25; value <= 20.001; value += 0.25) {
          const formatted = value.toFixed(2);
          const option = document.createElement('option');
          option.value = formatted;
          option.textContent = `${formatted} Δ`;
          select.append(option);
        }
      });
    }

    updatePrismDependencies() {
      this.querySelectorAll('[data-rx-prism-amount]').forEach((amount) => {
        const amountKey = amount.dataset.rxPrismAmount;
        const baseKey = `${amountKey}Base`;
        const base = this.querySelector(`[data-rx-prism-base="${amountKey}"]`);
        const hasPrism = String(this.state.clinical[amountKey] || '').trim() !== '';
        if (!base) return;
        base.disabled = !hasPrism;
        if (!hasPrism) { base.value = ''; delete this.state.clinical[baseKey]; }
      });
    }

    renderMeasurements() {
      const onePd = this.querySelector('[data-optical-rx-single-pd-choice]');
      if (onePd) onePd.hidden = false;
      const singleInput = this.querySelector('input[name^="optical-rx-pd-method-"][value="single"]');
      if (singleInput) singleInput.disabled = false;
      setHidden(this.querySelector('[data-optical-rx-pd-single]'), this.state.pdMethod !== 'single');
      setHidden(this.querySelector('[data-optical-rx-pd-dual]'), this.state.pdMethod !== 'dual');
      setHidden(this.querySelector('[data-optical-rx-pd-camera]'), this.state.pdMethod !== 'camera');
    }

    renderFitting() {
      const multifocal = this.state.vision === 'mf';
      setHidden(this.querySelector('[data-optical-rx-fitting]'), !multifocal);
      if (!multifocal || this.state.acknowledgement) this.clearFittingAcknowledgementError();
    }

    prefix(vision = this.state.vision, keyOverride = '') {
      if (vision === 'sv') return `CO-OPT-SV-${keyOverride || this.state.use || 'DIST'}`;
      return `CO-OPT-MF-${keyOverride || this.state.design || 'STD'}`;
    }

    selectedSku() {
      if (!this.state.vision || (!this.state.use && this.state.vision === 'sv') || (!this.state.design && this.state.vision === 'mf') || !this.state.category || !this.state.thickness) return '';
      const prefix = this.prefix();
      if (this.state.category === 'clear' && this.state.coating) return `${prefix}-CLEAR-${this.state.thickness}-${this.state.coating}`;
      if (this.state.category === 'light' && this.state.light && this.state.coating) return `${prefix}-LIGHT-${this.state.thickness}-${this.state.light}-${this.state.coating}`;
      if (this.state.category === 'sun' && this.state.sun) return `${prefix}-SUN-${this.state.thickness}-${this.state.sun}`;
      return '';
    }

    service(sku = this.selectedSku()) { return sku ? this.services[sku] || null : null; }
    baseThickness() { return this.minimumIndex === 't160' ? 'T160' : 'T150'; }
    referencePrice(prefix, suffix) { return Number(this.services[`${prefix}-${suffix}`]?.price || 0); }
    positiveDifference(high, low) { return high && low ? Math.max(0, high - low) : 0; }
    minimumServicePrice(vision = this.state.vision || 'sv', key = '') {
      const prefix = this.prefix(vision, key);
      const prices = Object.entries(this.services)
        .filter(([sku, item]) => sku.startsWith(`${prefix}-`) && item.available && (this.minimumIndex !== 't160' || /-T(?:160|167)-/.test(sku)))
        .map(([, item]) => Number(item.price))
        .filter(Number.isFinite);
      if (prices.length) return Math.min(...prices);
      return Number(vision === 'mf' ? this.dataset.mfFrom : this.dataset.svFrom) || 0;
    }

    updateDerivedPrices() {
      const prefix = this.prefix(this.state.vision || 'sv');
      const base = this.referencePrice(prefix, 'CLEAR-T150-HC');
      const set = (key, value, included = false) => {
        this.querySelectorAll(`[data-optical-rx-derived-price="${key}"]`).forEach((node) => { node.textContent = included || value === 0 ? 'Included' : `+${money(value, this.currency)}`; });
      };
      ['T160', 'T167'].forEach((thickness) => set(`thickness-${thickness}`, this.positiveDifference(this.referencePrice(prefix, `CLEAR-${thickness}-HC`), base)));
      ['HC', 'MTC', 'BLUE-MTC'].forEach((coating) => set(`coating-${coating}`, this.positiveDifference(this.referencePrice(prefix, `CLEAR-T150-${coating}`), base), coating === 'HC'));
      ['STD', 'PREM', 'XTRA'].forEach((technology) => set(`light-${technology}`, this.positiveDifference(this.referencePrice(prefix, `LIGHT-T150-${technology}-HC`), base)));
      ['TINT', 'POL', 'ULTRA'].forEach((type) => set(`sun-${type}`, this.positiveDifference(this.referencePrice(prefix, `SUN-T150-${type}`), base)));
      if (this.state.vision === 'mf' || !this.state.vision) {
        const standardBase = this.referencePrice(this.prefix('mf', 'STD'), 'CLEAR-T150-HC');
        ['WIDE', 'LARGEST'].forEach((design) => set(`mf-design-${design}`, this.positiveDifference(this.referencePrice(this.prefix('mf', design), 'CLEAR-T150-HC'), standardBase)));
      }
      const frame = this.currentFrame(); const service = this.service();
      const pathKey = this.state.vision === 'mf' ? this.state.design : this.state.use;
      const serviceFrom = this.minimumServicePrice(this.state.vision || 'sv', pathKey);
      const total = this.comboTotal(frame?.price, service?.price || serviceFrom);
      this.querySelectorAll('[data-optical-rx-live-total]').forEach((node) => {
        node.hidden = !this.state.vision;
        node.textContent = this.state.vision ? `Current total: ${money(total, this.currency)}` : '';
      });
      this.updateEntryButtons();
    }

    updateEntryButtons() {
      const frame = this.currentFrame();
      const minimum = this.minimumServicePrice('sv');
      const total = this.comboTotal(frame?.price, Number.isFinite(minimum) ? minimum : 0);
      document.querySelectorAll('[data-optical-prescription-open], [data-optical-prescription-launch]').forEach((button) => {
        button.disabled = !frame?.available;
        button.setAttribute('aria-disabled', String(!frame?.available));
        this.updateEntryButtonLabel(button, total, Boolean(frame?.available));
      });
      document.querySelectorAll('[data-optical-complete-price]').forEach((node) => { node.textContent = moneyWithCurrency(total, this.currency); });
      this.querySelectorAll('[data-optical-rx-vision-from]').forEach((node) => {
        const serviceFrom = this.minimumServicePrice(node.dataset.opticalRxVisionFrom);
        node.textContent = `Complete from ${money(this.comboTotal(frame?.price, Number.isFinite(serviceFrom) ? serviceFrom : 0), this.currency)}`;
      });
    }

    blueLightStartingTotal() {
      if (new URLSearchParams(location.search).get('purpose') !== 'blue-light-rx' || this.minimumIndex === 't160') return null;
      const service = this.services['CO-OPT-SV-DIST-CLEAR-T150-BLUE-MTC'];
      if (!service?.id || !service.available || !Number.isFinite(Number(service.price)) || Number(service.price) <= 0) return null;
      return Math.max(0, Number(this.currentFrame()?.price || 0) + Number(service.price) - this.comboDiscount);
    }

    updateEntryButtonLabel(button, total, available) {
      const unavailableLabel = 'Prescription glasses unavailable for this colour';
      const blueTotal = this.blueLightStartingTotal();
      const blueLabel = blueTotal === null ? '' : `Prescription blue-light from ${compactMoney(blueTotal, this.currency)}`;
      const fullLabel = available ? (blueLabel || `Build prescription glasses — from ${moneyWithCurrency(total, this.currency)} total`) : unavailableLabel;
      button.classList.toggle('is-unavailable', !available);

      const stickyDesktop = button.querySelector('[data-optical-sticky-prescription-desktop]');
      const stickyMobile = button.querySelector('[data-optical-sticky-prescription-mobile]');
      if (stickyDesktop && stickyMobile) {
        const stickyLabel = available ? (blueLabel || `Prescription glasses — from ${moneyWithCurrency(total, this.currency)}`) : 'Prescription unavailable';
        button.setAttribute('aria-label', stickyLabel);
        stickyDesktop.textContent = stickyLabel;
        stickyMobile.textContent = available ? (blueLabel || `Prescription — from ${compactMoney(total, this.currency)}`) : 'Prescription unavailable';
        return;
      }

      button.setAttribute('aria-label', fullLabel);

      const primary = button.querySelector('[data-optical-prescription-primary]');
      const secondary = button.querySelector('.optical-frame-section__button-secondary');
      if (primary && secondary) {
        primary.textContent = available ? (blueLabel || 'Build prescription glasses') : unavailableLabel;
        secondary.hidden = !available || Boolean(blueLabel);
        const separator = button.querySelector('.optical-frame-section__button-separator');
        if (separator) separator.hidden = !available || Boolean(blueLabel);
        return;
      }

      button.textContent = fullLabel;
    }

    updateProgress() {
      const stepNames = ['Choose your vision', 'Choose your lenses', 'Provide your prescription', 'Provide your PD', 'Review your glasses'];
      const stepName = stepNames[this.step - 1];
      this.querySelector('[data-optical-rx-progress-text]').textContent = `STEP ${this.step} OF 5`;
      this.querySelector('[data-optical-rx-progress-bar]').style.width = `${this.step * 20}%`;
      const progress = this.querySelector('[data-optical-rx-progress-track]');
      progress?.setAttribute('aria-valuenow', String(this.step));
      progress?.setAttribute('aria-valuetext', `Step ${this.step} of 5: ${stepName}`);
      this.querySelectorAll('[data-optical-rx-progress-segment]').forEach((segment, index) => {
        segment.classList.toggle('is-reached', index + 1 < this.step);
        segment.classList.toggle('is-current', index + 1 === this.step);
      });
      const stepNameNode = this.querySelector('[data-optical-rx-step-name]');
      if (stepNameNode) stepNameNode.textContent = stepName;
      const frame = this.currentFrame();
      const context = this.querySelector('[data-optical-rx-product-context]');
      if (context) context.textContent = [this.dataset.productTitle, frame?.title].filter(Boolean).join(' · ');
      const contextImage = this.querySelector('[data-optical-rx-context-image]');
      if (contextImage && frame) {
        contextImage.src = this.framePreview(frame);
        contextImage.alt = `${this.dataset.productTitle || 'Optical frame'}, ${frame.title || ''}`;
      }
      const frameCode = this.querySelector('[data-optical-rx-frame-code]');
      if (frameCode) {
        frameCode.hidden = !this.dataset.frameCode;
        frameCode.textContent = this.dataset.frameCode ? `Frame code: ${this.dataset.frameCode}` : '';
      }
      this.querySelectorAll('[data-optical-rx-step]').forEach((section) => { section.hidden = Number(section.dataset.opticalRxStep) !== this.step; });
      this.backButton.hidden = this.step === 1;
      const forwardLabels = ['Continue to lenses', 'Continue to prescription', 'Continue to PD', 'Continue to review'];
      this.nextButton.textContent = this.step === 5 ? `Add prescription glasses — ${this.currentTotalText()}` : forwardLabels[this.step - 1];
    }

    currentTotalText() {
      const frame = this.currentFrame(); const service = this.service();
      return money(this.comboTotal(frame?.price, service?.price), this.currency);
    }

    comboTotal(framePrice, servicePrice) {
      return Math.max(0, Number(framePrice || 0) + Number(servicePrice || 0) - this.comboDiscount + this.prismPrice());
    }

    prismPrice() { return this.state.prism === 'yes' ? Number(this.prismUpgrade.price || 5000) : 0; }

    prismStatus() { return this.state.prism === 'yes' ? 'Yes — prism treatment added' : this.state.prism === 'no' ? 'No prism' : '—'; }

    prismError() {
      if (!this.state.prism) return 'Choose whether your prescription includes prism.';
      if (this.state.prism !== 'yes') return '';
      if (!this.prismUpgrade.available || !this.prismUpgrade.id) return 'Prism treatment is temporarily unavailable online. Please contact City Optics for help.';
      if (this.state.prescriptionMethod !== 'manual') return '';
      const c = this.state.clinical;
      const pairs = [
        ['odPrismHorizontal', 'odPrismHorizontalBase'], ['odPrismVertical', 'odPrismVerticalBase'],
        ['osPrismHorizontal', 'osPrismHorizontalBase'], ['osPrismVertical', 'osPrismVerticalBase']
      ];
      let complete = 0;
      for (const [amountKey, baseKey] of pairs) {
        const amount = String(c[amountKey] || '').trim(); const base = String(c[baseKey] || '').trim();
        if (!amount && !base) continue;
        const number = Number(amount);
        if (!Number.isFinite(number) || number < 0.25 || number > 20 || Math.abs(number * 4 - Math.round(number * 4)) > .001 || !base) {
          return 'Enter each prism amount from 0.25Δ to 20.00Δ in 0.25 steps and choose its BASE direction.';
        }
        complete += 1;
      }
      return complete ? '' : 'Enter at least one complete prism amount and BASE direction, or choose No prism.';
    }

    updateForwardState() {
      if (!this.nextButton) return;
      const fittingAcknowledgementMissing = this.step === 5
        && this.state.vision === 'mf'
        && !this.state.acknowledgement
        && Boolean(this.currentFrame()?.available)
        && Boolean(this.service()?.available);
      this.nextButton.disabled = this.adding || fittingAcknowledgementMissing || !this.stepComplete(this.step, false);
      this.nextButton.setAttribute('aria-disabled', String(this.nextButton.disabled));
    }

    stepComplete(step, showErrors = false) {
      let message = '';
      if (step === 1) {
        if (!this.state.vision) message = 'Choose Single Vision or Multifocal.';
        else if (this.state.vision === 'sv' && !this.state.use) message = 'Choose Distance, Reading or Computer vision.';
        else if (this.state.vision === 'mf' && !this.state.design) message = 'Choose a multifocal design.';
      } else if (step === 2) {
        if (!this.state.category) message = 'Choose a lens category.';
        else if (!this.state.thickness) message = 'Choose a lens thickness.';
        else if (this.state.category === 'clear' && !this.state.coating) message = 'Choose a clear-lens coating.';
        else if (this.state.category === 'light' && (!this.state.light || !this.state.coating)) message = 'Choose a light-adaptive technology and coating.';
        else if (this.state.category === 'sun' && (!this.state.sun || !this.state.tint)) message = 'Choose a sunglass lens type and tint.';
        else if (!this.service()?.available) message = 'This lens configuration is not currently available. Please choose another option.';
      } else if (step === 3) message = this.prescriptionError();
      else if (step === 4) message = this.pdError();
      else if (step === 5) {
        if (!this.currentFrame()?.available) message = 'This frame colour is currently unavailable.';
        else if (!this.service()?.available) message = 'The selected lens configuration is unavailable.';
        else if (this.state.vision === 'mf' && !this.state.acknowledgement) message = 'Confirm that you’ll email the multifocal fitting photo after checkout.';
      }
      if (message && showErrors) {
        if (step === 5 && this.state.vision === 'mf' && !this.state.acknowledgement) {
          this.showFittingAcknowledgementError();
        }
        this.showError(message);
      }
      return !message;
    }

    showFittingAcknowledgementError() {
      const acknowledgement = this.querySelector('[data-optical-rx-ack]');
      const error = this.querySelector('[data-optical-rx-ack-error]');
      acknowledgement?.setAttribute('aria-invalid', 'true');
      if (error) error.hidden = false;
    }

    clearFittingAcknowledgementError() {
      const acknowledgement = this.querySelector('[data-optical-rx-ack]');
      const error = this.querySelector('[data-optical-rx-ack-error]');
      acknowledgement?.removeAttribute('aria-invalid');
      if (error) error.hidden = true;
    }

    prescriptionError() {
      if (!this.state.prescriptionMethod) return 'Choose how you will provide your prescription.';
      if (this.state.prescriptionMethod === 'upload' && !this.state.uploads.prescription) return 'Complete the secure prescription upload.';
      const prismIssue = this.prismError();
      if (prismIssue) return prismIssue;
      if (this.state.prescriptionMethod !== 'manual') return '';
      const c = this.state.clinical;
      if (c.odSph === undefined || c.odSph === '' || c.osSph === undefined || c.osSph === '') return 'Enter SPH for both eyes.';
      const quarterStep = (value, minimum, maximum) => {
        const number = Number(value);
        return Number.isFinite(number) && number >= minimum && number <= maximum && Math.abs((number - minimum) / 0.25 - Math.round((number - minimum) / 0.25)) < 0.0001;
      };
      if (!quarterStep(c.odSph, -12, 8) || !quarterStep(c.osSph, -12, 8)) return 'Enter SPH from -12.00 to +8.00 in 0.25 steps.';
      if (!quarterStep(c.odCyl || 0, -6, 0) || !quarterStep(c.osCyl || 0, -6, 0)) return 'Enter CYL in minus-cylinder notation from 0.00 to -6.00 in 0.25 steps.';
      const validAxis = (value) => Number.isInteger(Number(value)) && Number(value) >= 1 && Number(value) <= 180;
      if (Number(c.odCyl || 0) !== 0 && !validAxis(c.odAxis)) return 'Enter the right-eye AXIS as a whole number from 1 to 180.';
      if (Number(c.osCyl || 0) !== 0 && !validAxis(c.osAxis)) return 'Enter the left-eye AXIS as a whole number from 1 to 180.';
      if (this.state.vision === 'mf' && (!quarterStep(c.odAdd, .25, 5) || !quarterStep(c.osAdd, .25, 5))) return 'Enter ADD from +0.25 to +5.00 in 0.25 steps for both eyes.';
      return '';
    }

    pdError() {
      if (!this.state.pdMethod) return 'Choose how you will provide your PD.';
      if (this.state.pdMethod === 'later') return '';
      const c = this.state.clinical;
      if (this.state.pdMethod === 'single') return Number(c.pd) >= 45 && Number(c.pd) <= 80 ? '' : 'Enter one PD between 45 and 80 mm.';
      if (this.state.pdMethod === 'dual') return Number(c.pdRight) >= 20 && Number(c.pdRight) <= 45 && Number(c.pdLeft) >= 20 && Number(c.pdLeft) <= 45 ? '' : 'Enter right and left PD values between 20 and 45 mm.';
      if (this.state.pdMethod === 'camera') {
        const hasDual = Number(c.pdRight) >= 20 && Number(c.pdLeft) >= 20;
        const hasSingle = Number(c.pd) >= 45;
        if (this.state.vision === 'mf' && !hasDual) return 'Multifocal lenses need separate right and left PD values. Complete the camera measurement or enter two PDs.';
        return hasDual || hasSingle ? '' : 'Complete the camera PD measurement.';
      }
      return '';
    }

    forward() {
      if (!this.stepComplete(this.step, true)) return;
      if (this.step === 5) { this.addToCart(); return; }
      this.goTo(this.step + 1);
    }

    backward() { if (this.step > 1) this.goTo(this.step - 1); }
    goTo(step, focus = true) {
      this.step = Math.max(1, Math.min(5, step));
      this.clearError();
      this.render();
      this.body?.scrollTo({ top: 0, behavior: 'instant' });
      if (focus) requestAnimationFrame(() => this.querySelector(`[data-optical-rx-step="${this.step}"] h3`)?.focus());
    }

    showError(message) {
      this.errorNode.hidden = false;
      this.errorNode.textContent = message;
      this.errorNode.focus();
    }
    clearError() { if (this.errorNode) { this.errorNode.hidden = true; this.errorNode.textContent = ''; } }
    setExternalStatus(message) {
      const node = document.querySelector('[data-optical-status]');
      if (node) { node.textContent = message; node.dataset.blocked = 'true'; }
    }

    renderReview() {
      const frame = this.currentFrame(); const service = this.service();
      const image = this.querySelector('[data-optical-rx-review-image]');
      if (image && frame) { image.src = this.framePreview(frame); image.alt = `${this.dataset.productTitle}, ${frame.title}`; }
      const title = this.querySelector('[data-optical-rx-review-title]'); if (title) title.textContent = this.dataset.productTitle || '';
      const colour = this.querySelector('[data-optical-rx-review-colour]'); if (colour) colour.textContent = frame?.title || '';
      const vision = this.state.vision === 'mf' ? 'Multifocal' : 'Single Vision';
      const use = this.state.vision === 'mf' ? this.designLabel() : this.useLabel();
      const lensRows = [row('Vision', vision), row(this.state.vision === 'mf' ? 'Design' : 'Use', use), row('Lens category', this.categoryLabel()), row('Thickness', this.thicknessLabel())];
      if (this.state.category === 'clear' || this.state.category === 'light') lensRows.push(row('Coating', this.coatingDisplayLabel()));
      if (this.state.category === 'light') lensRows.push(row('Technology', this.lightLabel()));
      if (this.state.category === 'sun') { lensRows.push(row('Lens type', this.sunLabel())); lensRows.push(row('Tint', this.state.tint)); }
      const lensNode = this.querySelector('[data-optical-rx-review-lenses]'); if (lensNode) lensNode.innerHTML = lensRows.join('');
      const measurementRows = [row('Prescription', this.prescriptionStatus()), row('Prism', this.prismStatus()), row('PD', this.pdStatus())];
      const measurementNode = this.querySelector('[data-optical-rx-review-measurements]'); if (measurementNode) measurementNode.innerHTML = measurementRows.join('');
      const pricing = this.priceBreakdown();
      const pricingNode = this.querySelector('[data-optical-rx-review-pricing]'); if (pricingNode) pricingNode.innerHTML = pricing.map((item) => row(item.label, money(item.price, this.currency))).join('');
      if (this.step === 5 && service) this.nextButton.textContent = `Add prescription glasses — ${this.currentTotalText()}`;
    }

    priceBreakdown() {
      const frame = this.currentFrame(); const service = this.service();
      if (!frame || !service) return [];
      const standardPrefix = this.state.vision === 'mf' ? this.prefix('mf', 'STD') : this.prefix('sv');
      const selectedPrefix = this.prefix();
      const standardBase = this.referencePrice(standardPrefix, 'CLEAR-T150-HC');
      const designBase = this.referencePrice(selectedPrefix, 'CLEAR-T150-HC');
      const items = [{ label: 'Frame', price: Number(frame.price) }, { label: this.state.vision === 'mf' ? 'Multifocal lenses' : 'Single Vision lenses', price: standardBase }];
      if (designBase > standardBase) items.push({ label: this.designLabel(), price: designBase - standardBase });
      const thicknessBase = this.referencePrice(selectedPrefix, `CLEAR-${this.state.thickness}-HC`);
      const thicknessUpgrade = Math.max(0, thicknessBase - designBase);
      if (thicknessUpgrade > 0) items.push({ label: this.thicknessLabel(), price: thicknessUpgrade });

      let pathwayBase = thicknessBase;
      if (this.state.category === 'clear') {
        const coatingUpgrade = Math.max(0, Number(service.price) - thicknessBase);
        if (coatingUpgrade > 0) items.push({ label: this.coatingDisplayLabel(), price: coatingUpgrade });
      } else if (this.state.category === 'light') {
        const technologyBase = this.referencePrice(selectedPrefix, `LIGHT-${this.state.thickness}-${this.state.light}-HC`);
        const technologyUpgrade = Math.max(0, technologyBase - thicknessBase);
        if (technologyUpgrade > 0) items.push({ label: this.lightLabel(), price: technologyUpgrade });
        pathwayBase = technologyBase;
        const coatingUpgrade = Math.max(0, Number(service.price) - pathwayBase);
        if (coatingUpgrade > 0) items.push({ label: this.coatingDisplayLabel(), price: coatingUpgrade });
      } else if (this.state.category === 'sun') {
        const sunUpgrade = Math.max(0, Number(service.price) - thicknessBase);
        if (sunUpgrade > 0) items.push({ label: this.sunLabel(), price: sunUpgrade });
      }
      if (this.comboDiscount > 0) items.push({ label: 'Complete glasses frame offer', price: -this.comboDiscount });
      if (this.state.prism === 'yes') items.push({ label: 'Prism treatment', price: this.prismPrice() });
      items.push({ label: 'Total', price: this.comboTotal(frame.price, service.price) });
      return items;
    }

    useLabel() { return ({ DIST: 'Distance', READ: 'Reading', COMP: 'Computer' })[this.state.use] || '—'; }
    designLabel() { return ({ STD: 'Standard Design', WIDE: 'Comfort Design', LARGEST: 'Ultimate Design' })[this.state.design] || '—'; }
    categoryLabel() { return ({ clear: 'Clear Lenses', light: 'Light-Adaptive Lenses', sun: 'Prescription Sunglasses' })[this.state.category] || '—'; }
    thicknessLabel() { return ({ T150: 'Standard 1.50', T160: 'Thin 1.60', T167: 'Extra Thin 1.67' })[this.state.thickness] || '—'; }
    coatingLabel() { return ({ HC: 'Hard Coat', MTC: 'Anti-Reflective Multi-Coat', 'BLUE-MTC': 'Blue Filter + Premium Multi-Coat' })[this.state.coating] || '—'; }
    coatingDisplayLabel() { return this.state.coating === 'BLUE-MTC' ? 'Blue Filter (built-in)' : this.coatingLabel(); }
    lightLabel() { return ({ STD: 'Standard Light-Adaptive', PREM: 'Premium Light-Adaptive', XTRA: 'Transitions® XTRActive®' })[this.state.light] || '—'; }
    sunLabel() { return ({ TINT: 'Standard Tint', POL: 'Polarised', ULTRA: 'Ultra Polarised' })[this.state.sun] || '—'; }
    prescriptionStatus() { return ({ upload: 'Uploaded securely', manual: 'Entered manually', later: 'Required — pending' })[this.state.prescriptionMethod] || '—'; }
    pdStatus() {
      if (this.state.pdMethod === 'later') return 'Required — pending';
      if (this.state.pdMethod === 'single') return `${this.state.clinical.pd || '—'} mm (one PD)`;
      if (this.state.pdMethod === 'dual' || this.state.pdMethod === 'camera') {
        if (this.state.clinical.pdRight && this.state.clinical.pdLeft) return `${this.state.clinical.pdRight} / ${this.state.clinical.pdLeft} mm`;
        if (this.state.clinical.pd) return `${this.state.clinical.pd} mm`;
      }
      return '—';
    }

    clinicalProperties() {
      if (this.state.prescriptionMethod !== 'manual') return {};
      const c = this.state.clinical;
      return {
        '_Right Eye / OD SPH': c.odSph || '', '_Right Eye / OD CYL': c.odCyl || '0.00', '_Right Eye / OD AXIS': c.odAxis || '',
        '_Left Eye / OS SPH': c.osSph || '', '_Left Eye / OS CYL': c.osCyl || '0.00', '_Left Eye / OS AXIS': c.osAxis || '',
        ...(this.state.vision === 'mf' ? { '_Right Eye / OD ADD': c.odAdd || '', '_Left Eye / OS ADD': c.osAdd || '' } : {}),
        ...(this.state.prism === 'yes' ? {
          '_Right Eye / OD Horizontal Prism': c.odPrismHorizontal || '', '_Right Eye / OD Horizontal Base': c.odPrismHorizontalBase || '',
          '_Right Eye / OD Vertical Prism': c.odPrismVertical || '', '_Right Eye / OD Vertical Base': c.odPrismVerticalBase || '',
          '_Left Eye / OS Horizontal Prism': c.osPrismHorizontal || '', '_Left Eye / OS Horizontal Base': c.osPrismHorizontalBase || '',
          '_Left Eye / OS Vertical Prism': c.osPrismVertical || '', '_Left Eye / OS Vertical Base': c.osPrismVerticalBase || ''
        } : {})
      };
    }

    pdProperties() {
      const c = this.state.clinical;
      if (this.state.pdMethod === 'single') return { '_PD': c.pd || '' };
      if (this.state.pdMethod === 'dual' || this.state.pdMethod === 'camera') return {
        ...(c.pd ? { '_PD': c.pd } : {}), ...(c.pdRight ? { '_Right PD': c.pdRight } : {}), ...(c.pdLeft ? { '_Left PD': c.pdLeft } : {}),
        ...(this.state.pdSource ? { '_PD source': this.state.pdSource } : {}),
        ...(this.state.pdProvenance ? { '_PD measurement review': 'Validation required' } : {})
      };
      return {};
    }

    buildCartItems() {
      const frame = this.currentFrame(); const service = this.service(); const sku = this.selectedSku();
      if (!frame?.available || !service?.available || !sku) throw new Error('configuration_unavailable');
      const groupId = globalThis.crypto?.randomUUID?.() || `opt-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
      const shared = { '_city_rx_sunglasses_group': groupId, '_city_rx_order_type': 'optical', '_city_rx_sunglasses_configuration': sku, '_city_rx_sunglasses_frame_variant': String(frame.id), '_city_rx_preview_image': this.framePreview(frame) };
      const multifocalPhotoRequired = this.state.vision === 'mf';
      const prescriptionPending = this.state.prescriptionMethod === 'later';
      const pdPending = this.state.pdMethod === 'later';
      const serviceProperties = {
        ...shared, '_city_rx_sunglasses_service': 'true', '_city_rx_restore_version': '1',
        'Vision type': this.state.vision === 'mf' ? 'Multifocal' : 'Single Vision',
        ...(this.state.vision === 'mf' ? { 'Multifocal design': this.designLabel() } : { 'Intended use': this.useLabel() }),
        'Lens category': this.categoryLabel(), 'Lens thickness': this.thicknessLabel(),
        ...((this.state.category === 'clear' || this.state.category === 'light') ? { 'Lens coating': this.coatingLabel() } : {}),
        ...(this.state.category === 'light' ? { 'Lens technology': this.lightLabel() } : {}),
        ...(this.state.category === 'sun' ? { 'Lens type': this.sunLabel(), 'Lens colour': this.state.tint } : {}),
        'Prescription status': this.prescriptionStatus(), 'PD status': this.pdStatus(),
        'Prism': this.state.prism === 'yes' ? 'Yes — prism treatment required' : 'No',
        ...(this.state.uploads.prescription ? { '_Prescription Upload': this.state.uploads.prescription } : {}),
        ...(multifocalPhotoRequired ? { 'Multifocal fitting photo': 'Required after checkout', 'Fitting photo status': 'Awaiting customer photo' } : {}),
        '_Optical Review Status': multifocalPhotoRequired ? 'Hold — fitting photo pending' : 'Pending optical review',
        '_Production Ready': 'No — optical review required',
        '_Staff Action': multifocalPhotoRequired ? 'Email customer for front-facing fitting photo; approve or request retake before production' : 'Review prescription, PD and frame suitability before production',
        '_city_rx_prescription_method': this.state.prescriptionMethod === 'later' ? 'email' : this.state.prescriptionMethod,
        '_city_rx_pd_method': pdPending ? 'unknown' : this.state.pdMethod,
        ...(prescriptionPending ? { '_Prescription follow-up': 'Required' } : {}),
        ...(pdPending ? { '_PD follow-up': 'Required' } : {}),
        ...this.clinicalProperties(), ...this.pdProperties()
      };
      const items = [
          { id: Number(frame.id), quantity: 1, properties: { ...shared, '_city_rx_sunglasses_parent': 'true' } },
          { id: Number(service.id), quantity: 1, properties: serviceProperties }
        ];
      if (this.state.prism === 'yes') {
        if (!this.prismUpgrade.available || !this.prismUpgrade.id) throw new Error('configuration_unavailable');
        items.push({
          id: this.prismUpgrade.id,
          quantity: 1,
          properties: { ...shared, '_city_rx_prism_upgrade': 'true', 'Prism treatment': 'One pair' }
        });
      }
      return { groupId, items };
    }

    async addToCart() {
      if (this.adding) return;
      if (this.state.vision === 'mf' && !this.state.acknowledgement) {
        this.showFittingAcknowledgementError();
        this.showError('Confirm that you’ll email the multifocal fitting photo after checkout.');
        this.querySelector('[data-optical-rx-ack]')?.focus();
        return;
      }
      if (!this.stepComplete(5, true)) return;
      this.adding = true; this.nextButton.textContent = 'Adding…'; this.updateForwardState();
      let groupId = ''; let response = null;
      try {
        const built = this.buildCartItems(); groupId = built.groupId;
        const drawer = document.querySelector('cart-drawer');
        const sectionIds = typeof drawer?.getSectionsToRender === 'function' ? drawer.getSectionsToRender().map((section) => section.id).filter(Boolean) : ['cart-drawer', 'cart-icon-bubble'];
        const root = window.Shopify?.routes?.root || '/';
        const request = await fetch(window.routes?.cart_add_url || `${root}cart/add.js`, { method: 'POST', cache: 'no-store', credentials: 'same-origin', headers: { Accept: 'application/json', 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: JSON.stringify({ items: built.items, sections: sectionIds.join(','), sections_url: window.location.pathname }) });
        response = await request.json().catch(() => ({}));
        if (!request.ok || response.status) throw new Error(response.description || 'cart_add_failed');
        const verify = await fetch(`${root}cart.js`, { cache: 'no-store', credentials: 'same-origin' });
        const cart = await verify.json();
        const grouped = (cart.items || []).filter((item) => item.properties?._city_rx_sunglasses_group === groupId);
        const expectedCount = this.state.prism === 'yes' ? 3 : 2;
        if (grouped.length !== expectedCount || !grouped.some((item) => item.properties?._city_rx_sunglasses_parent === 'true') || !grouped.some((item) => item.properties?._city_rx_sunglasses_service === 'true') || (this.state.prism === 'yes' && !grouped.some((item) => item.properties?._city_rx_prism_upgrade === 'true'))) throw new Error('cart_add_incomplete');
        this.close({ restoreFocus: false });
        if (window.CityCartEdit?.refreshCartDrawer) await window.CityCartEdit.refreshCartDrawer(true, 1);
        else if (drawer?.renderContents && response.sections) drawer.renderContents(response);
        else { document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'optical-prescription' } })); drawer?.open?.(); }
      } catch (error) {
        const rolledBack = !groupId || await this.rollbackGroup(groupId).catch(() => false);
        if (error.message === 'configuration_unavailable') {
          this.showError('This frame or lens configuration is no longer available. Review your selections and try again.');
        } else if (rolledBack) {
          this.showError('We could not add the complete prescription-glasses order. Nothing incomplete was left in your cart. Please try again.');
        } else {
          this.showError('We could not add the complete prescription-glasses order or confirm its removal. Please check your cart and remove any incomplete prescription-glasses items before checkout, or contact our team for help.');
        }
      } finally {
        this.adding = false; this.render();
      }
    }

    async rollbackGroup(groupId) {
      const root = window.Shopify?.routes?.root || '/';
      const request = await fetch(`${root}cart.js`, { cache: 'no-store', credentials: 'same-origin' });
      if (!request.ok) throw new Error('cart_rollback_read_failed');
      const cart = await request.json(); const updates = {};
      (cart.items || []).forEach((item) => { if (item.properties?._city_rx_sunglasses_group === groupId) updates[item.key] = 0; });
      if (Object.keys(updates).length) {
        const removal = await fetch(`${root}cart/update.js`, { method: 'POST', cache: 'no-store', credentials: 'same-origin', headers: { Accept: 'application/json', 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: JSON.stringify({ updates }) });
        if (!removal.ok) throw new Error('cart_rollback_update_failed');
      }
      const verification = await fetch(`${root}cart.js`, { cache: 'no-store', credentials: 'same-origin' });
      if (!verification.ok) throw new Error('cart_rollback_verify_failed');
      const verifiedCart = await verification.json();
      if ((verifiedCart.items || []).some((item) => item.properties?._city_rx_sunglasses_group === groupId)) throw new Error('cart_rollback_incomplete');
      return true;
    }

    adoptCloudliftUploads() {
      this.querySelectorAll('[data-optical-rx-upload-slot]').forEach((slot) => {
        const type = slot.dataset.opticalRxUploadSlot;
        if (slot.querySelector('.cl-upload--wrapper, .filepond--root')) {
          slot.querySelector('.optical-rx__upload-waiting')?.remove();
          if (!slot.dataset.opticalRxUploadBound) {
            slot.dataset.opticalRxUploadBound = 'true';
            slot.addEventListener('change', () => this.captureUpload(slot, type));
            slot.addEventListener('input', () => this.captureUpload(slot, type));
          }
        }
        this.captureUpload(slot, type);
      });
      this.updateUploadAvailability(Boolean(this.uploadCheckComplete));
    }

    getUploadProviderSignature() {
      return [...this.querySelectorAll('[data-optical-rx-upload-slot]')]
        .map((slot) => `${slot.dataset.opticalRxUploadSlot}:${Boolean(slot.querySelector('.cl-upload--wrapper, .filepond--root'))}`)
        .join('|');
    }

    updateUploadAvailability(finalCheck = false) {
      if (finalCheck) this.uploadCheckComplete = true;
      ['prescription'].forEach((type) => {
        const slot = this.querySelector(`[data-optical-rx-upload-slot="${type}"]`);
        const choice = this.querySelector(`[data-optical-rx-upload-choice="${type}"]`);
        const input = choice?.querySelector('input');
        const copy = choice?.querySelector('[data-optical-rx-upload-choice-copy]');
        const available = Boolean(slot?.querySelector('.cl-upload--wrapper, .filepond--root'));
        if (!input || !copy) return;
        if (available) {
          input.disabled = false;
          copy.textContent = 'Recommended';
          return;
        }
        if (!finalCheck) return;
        input.disabled = true;
        copy.textContent = 'Choose Send later';
        if (type === 'prescription' && this.state.prescriptionMethod === 'upload') this.state.prescriptionMethod = '';
      });
      this.syncSelections();
      this.updateForwardState();
    }

    captureUpload(target, forcedType = '') {
      const slot = target?.matches?.('[data-optical-rx-upload-slot]') ? target : target?.closest?.('[data-optical-rx-upload-slot]');
      const type = forcedType || slot?.dataset.opticalRxUploadSlot || 'prescription';
      let uploadedValue = '';
      slot?.querySelectorAll('input, textarea').forEach((field) => {
        const value = String(field.value || field.dataset.url || field.getAttribute('data-value') || '').trim();
        if (this.isUploadReference(value)) uploadedValue = value;
      });
      this.state.uploads[type] = uploadedValue;
      const hidden = this.querySelector(`[data-optical-rx-upload-value="${type}"]`); if (hidden) hidden.value = this.state.uploads[type];
      this.updateForwardState();
    }

    isUploadReference(value) {
      try {
        const url = new URL(String(value || ''));
        return url.protocol === 'https:' && (url.hostname === 'cloudlift.app' || url.hostname.endsWith('.cloudlift.app'));
      } catch (_) {
        return false;
      }
    }

    loadAsset(tag, attributes, key) {
      window.CityOpticsOpticalPdAssets ||= {};
      if (window.CityOpticsOpticalPdAssets[key]) return window.CityOpticsOpticalPdAssets[key];
      window.CityOpticsOpticalPdAssets[key] = new Promise((resolve, reject) => {
        const existing = tag === 'script' ? document.querySelector(`script[src="${attributes.src}"]`) : document.querySelector(`link[href="${attributes.href}"]`);
        if (existing && (tag !== 'script' || window.CityOpticsPdMeasurement)) { resolve(); return; }
        const node = existing || document.createElement(tag); Object.entries(attributes).forEach(([name, value]) => node.setAttribute(name, value));
        node.addEventListener('load', resolve, { once: true }); node.addEventListener('error', reject, { once: true });
        if (!existing) document.head.append(node);
      });
      return window.CityOpticsOpticalPdAssets[key];
    }

    async openPdCamera(opener) {
      const root = this.querySelector('[data-optical-rx-pd-tool]'); if (!root) return;
      try {
        await Promise.all([this.loadAsset('link', { rel: 'stylesheet', href: this.dataset.pdStyles }, 'styles'), window.CityOpticsPdMeasurement ? Promise.resolve() : this.loadAsset('script', { src: this.dataset.pdScript }, 'script')]);
        if (!this.pdController) this.pdController = window.CityOpticsPdMeasurement.create(root, { runtimeUrl: this.dataset.pdRuntime, wasmUrl: this.dataset.pdWasm, modelUrl: this.dataset.pdModel, manualCornersEnabled: true, manualCornersDelayMs: 7000, modelLoadTimeoutMs: 20000, limits: { singleMin: 45, singleMax: 80, dualMin: 20, dualMax: 45 } });
        this.pdOpener = opener; this.pdController.open();
      } catch (_) { this.showError('The camera PD tool could not load. Enter your PD manually or select Send later.'); }
    }

    applyPdMeasurement(measurement) {
      const mapped = window.CityOpticsPdMeasurement?.normaliseCameraPdMeasurement?.(measurement); if (!mapped) return;
      this.state.pdMethod = 'camera'; this.state.pdSource = mapped.pdSource || 'camera'; this.state.pdProvenance = mapped.provenance || measurement;
      delete this.state.clinical.pd; delete this.state.clinical.pdRight; delete this.state.clinical.pdLeft; Object.assign(this.state.clinical, mapped.clinical || {});
      this.syncRadio('optical-rx-pd-method-', 'camera'); this.render(); this.pdOpener?.focus?.();
    }
  }

  if (!customElements.get('optical-prescription-builder')) customElements.define('optical-prescription-builder', OpticalPrescriptionBuilder);
})();
